/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 25/08/2026
  PREVIOUSLY: 11/08/2026, 26/06/2026
  ========================================================================
  Impact base-theme reference: the vendor URL in assets/documentation.txt.
  Gro design spec: 05-design/00-web-design-system (vault root relative).
  Custom Gro modules are documented in snippets/_gro-docs-*.liquid.
  ========================================================================

  gro-quiz.js - the whole interactive behaviour of the "Quiz -
  Recommendations" section: it reads the hidden config markup that Liquid
  wrote from the section blocks, renders one question at a time, scores the
  answers, optionally captures an email, and renders the winning result
  cards.

  ------------------------------------------------------------------------
  OWNERSHIP
  ------------------------------------------------------------------------
  OWNS   The runtime step machine, keyboard behaviour of the option
         radiogroup, progress width/aria, the optional email step,
         scoring, and the DOM of the result cards it builds.
  DOES NOT OWN
         The markup contract or any merchant setting. Both live in
         sections/gro-quiz-recommendation.liquid (module
         "section - gro-quiz-recommendation"). It also owns no styling:
         every class it writes is styled by assets/gro-quiz.css
         (module "asset - gro-quiz"). Adding a class here without adding
         the rule there ships an unstyled element.

  ------------------------------------------------------------------------
  GRO MODULE CONTRACT - read before editing
  ------------------------------------------------------------------------
  BOOTSTRAP
    The last line runs immediately on parse and constructs one GroQuiz per
    `.gro-quiz` element. The section tag loads this file with `defer`, and
    only in the live branch, so design mode never runs it. There is no
    `shopify:section:load` listener: a section re-render in the editor does
    not re-initialise, by design, because the editor branch is static.

  DOM CONTRACT - these selectors are a cross-file agreement with
  sections/gro-quiz-recommendation.liquid. Renaming one here without
  renaming it there silently breaks the quiz with no console error:
    .gro-quiz                      root, carries data-section-id
    .gro-quiz__config              hidden config wrapper
    .gro-quiz__step-def            data-key, data-question
    .gro-quiz__option-def          data-label, data-value, data-recommendation
    .gro-quiz__result-def          data-key, data-title, data-cta-label,
                                   data-cta-url, data-image, data-image-alt
    .gro-quiz__result-desc-src     result description text source
    #gro-quiz-steps-<sectionId>    where each rendered step is written
    #gro-quiz-email-<sectionId>    optional email step, starts hidden
    #gro-quiz-results-<sectionId>  results panel, starts hidden
    #gro-quiz-results-grid-<sectionId>  where result cards are appended
    .gro-quiz__progress            aria-valuenow target
    .gro-quiz__progress-bar        width target
    .js-gro-quiz-back / .js-gro-quiz-next / .js-gro-quiz-skip-email

  SCORING
    One point per answered step to that option's `recommendation` string.
    Highest total wins. A tie renders every tied result, and an answer set
    with no recommendation strings at all renders every result block. Both
    fallbacks are deliberate: a visitor never sees an empty results panel.

  RESULT KEYS
    A result block is matched by its `data-key` equalling the winning
    recommendation string, so the option recommendation values and the
    result keys must be spelled identically. A mismatch is not an error,
    it silently degrades to "show all results".

  EMAIL STEP
    Posts the form to /contact with a hidden `contact[note]` field carrying
    the answers. The fetch is fire and forget: a network failure must not
    block the visitor from seeing their result, so the result renders in
    `finally`. A repeated submit appends another note input, which is
    tolerated because the step is hidden immediately after.

  EVENTS
    Dispatches `quiz:complete` on `document`, bubbling, with
    detail { answers, recommendations }. Treat it as a public hook: analytics
    or another module may already listen for it.

  STYLE
    Modern JS only (const/let, arrow functions, optional chaining, no var).
    CSS over JS: presentation belongs in gro-quiz.css, not in inline style
    writes. The one exception is the progress bar width, which is a live
    value. No em dashes anywhere, hyphens only.

  ------------------------------------------------------------------------
  DEPENDENCIES
  ------------------------------------------------------------------------
    sections/gro-quiz-recommendation.liquid  emits the config markup and
                                             loads this file
    assets/gro-quiz.css                      styles every class written here
    /contact                                 Shopify contact form endpoint
                                             used by the optional email step
    No metaobjects, no metafields, no app markup, no template assumptions.

  ------------------------------------------------------------------------
  DELIBERATE EXEMPTIONS
  ------------------------------------------------------------------------
    AGENTS.md rule 5 (Anchor ID) does not apply: this is an asset, not a
    section. The anchor span is emitted by
    sections/gro-quiz-recommendation.liquid.
    No other exemptions.
  ========================================================================
*/

/*
  QUIZ_DEV_MODE - MUST BE false IN ANY PUSHED BUILD.

  When true: skips _scoreAndFind() entirely and renders the FIRST result
  block regardless of the visitor's answers, so the result UI can be
  reviewed without completing the quiz.

  This shipped to production as true and stayed live from 26/06/2026 to
  11/08/2026 - every visitor who finished the quiz saw the same
  recommendation. Do not leave it on to "check something quickly": the
  quiz is a lead-capture path on a medical-adjacent site, and a wrong
  recommendation is a clinical-trust problem, not a cosmetic one.

  Turn it on locally only, and turn it off in the same sitting.
*/
const QUIZ_DEV_MODE = false;

class GroQuiz {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    this.answers = {};
    this.currentStep = 0;
    this.navigating = false;

    this.config = this._parseConfig();
    this.steps = this.config.steps || [];
    this.totalSteps = this.steps.length;

    this.stepsEl = container.querySelector(`#gro-quiz-steps-${this.sectionId}`);
    this.progressEl = container.querySelector('.gro-quiz__progress');
    this.progressBarEl = container.querySelector('.gro-quiz__progress-bar');
    this.emailStepEl = container.querySelector(`#gro-quiz-email-${this.sectionId}`);
    this.resultsEl = container.querySelector(`#gro-quiz-results-${this.sectionId}`);
    this.resultsGridEl = container.querySelector(`#gro-quiz-results-grid-${this.sectionId}`);

    if (this.totalSteps > 0) this._init();
  }

  /* ---- Config: read from DOM (built by Liquid from section.blocks) ---- */

  _parseConfig() {
    const configEl = this.container.querySelector('.gro-quiz__config');
    if (!configEl) return { steps: [] };

    const steps = [...configEl.querySelectorAll('.gro-quiz__step-def')].map((stepEl) => ({
      key: stepEl.dataset.key,
      question: stepEl.dataset.question,
      options: [...stepEl.querySelectorAll('.gro-quiz__option-def')].map((optEl) => ({
        label: optEl.dataset.label,
        value: optEl.dataset.value,
        recommendation: optEl.dataset.recommendation || '',
      })),
    }));

    return { steps };
  }

  _init() {
    this._renderStep(0);
    this._bindEmailCapture();
  }

  /* ---- Step rendering ---- */

  _renderStep(index) {
    const step = this.steps[index];
    if (!step) return;

    this._updateProgress(index);

    const isLast = index === this.totalSteps - 1;
    const selected = this.answers[step.key]?.value;
    const questionId = `gro-quiz-q-${this.sectionId}-${index}`;

    const stepEl = document.createElement('div');
    stepEl.className = 'gro-quiz__step';
    stepEl.dataset.stepIndex = index;

    stepEl.innerHTML = `
      <h3 id="${questionId}" class="gro-quiz__question" tabindex="-1">${step.question}</h3>
      <div class="gro-quiz__options" role="radiogroup" aria-labelledby="${questionId}">
        ${step.options.map((opt) => `
          <button
            class="gro-quiz__option${selected === opt.value ? ' is-selected' : ''}"
            role="radio"
            aria-checked="${selected === opt.value ? 'true' : 'false'}"
            tabindex="${selected === opt.value ? '0' : (step.options.indexOf(opt) === 0 && !selected ? '0' : '-1')}"
            data-value="${opt.value}"
            data-recommendation="${opt.recommendation}"
            data-step-key="${step.key}"
          >${opt.label}</button>
        `).join('')}
      </div>
      <div class="gro-quiz__nav">
        ${index > 0
          ? `<button class="gro-quiz__btn gro-quiz__btn--ghost js-gro-quiz-back" aria-label="Back to previous question">Back</button>`
          : '<span></span>'
        }
        <button
          class="gro-quiz__btn gro-quiz__btn--primary js-gro-quiz-next"
          ${!selected ? 'disabled' : ''}
          aria-label="${isLast ? 'See my recommendation' : 'Next question'}"
        >${isLast ? 'See my recommendation' : 'Next'}</button>
      </div>
    `;

    this.stepsEl.innerHTML = '';
    this.stepsEl.appendChild(stepEl);
    this._bindStepEvents(stepEl, step);

    if (this.navigating) {
      stepEl.querySelector('.gro-quiz__question').focus();
    }
    this.navigating = true;
  }

  _bindStepEvents(stepEl, step) {
    const options = [...stepEl.querySelectorAll('.gro-quiz__option')];

    options.forEach((btn, i) => {
      btn.addEventListener('click', () => this._selectOption(btn, options));
      btn.addEventListener('keydown', (e) => this._handleOptionKey(e, btn, options, i));
    });

    stepEl.querySelector('.js-gro-quiz-back')?.addEventListener('click', () => this._goBack());
    stepEl.querySelector('.js-gro-quiz-next')?.addEventListener('click', () => this._goNext());
  }

  _handleOptionKey(e, btn, options, index) {
    const last = options.length - 1;
    let target = null;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      target = options[index < last ? index + 1 : 0];
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      target = options[index > 0 ? index - 1 : last];
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._selectOption(btn, options);
      return;
    }

    if (target) {
      options.forEach((o) => o.setAttribute('tabindex', '-1'));
      target.setAttribute('tabindex', '0');
      target.focus();
    }
  }

  _selectOption(btn, allOptions) {
    const key = btn.dataset.stepKey;
    const value = btn.dataset.value;
    const recommendation = btn.dataset.recommendation;

    this.answers[key] = { value, recommendation };

    allOptions.forEach((o) => {
      o.classList.remove('is-selected');
      o.setAttribute('aria-checked', 'false');
      o.setAttribute('tabindex', '-1');
    });

    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');
    btn.setAttribute('tabindex', '0');

    const nextBtn = this.stepsEl.querySelector('.js-gro-quiz-next');
    if (nextBtn) nextBtn.disabled = false;
  }

  _goNext() {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
      this._renderStep(this.currentStep);
    } else {
      this._completeQuiz();
    }
  }

  _goBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this._renderStep(this.currentStep);
    }
  }

  /* ---- Progress ---- */

  _updateProgress(stepIndex) {
    const pct = Math.round((stepIndex / this.totalSteps) * 100);
    this.progressBarEl.style.width = `${pct}%`;
    this.progressEl.setAttribute('aria-valuenow', String(pct));
  }

  /* ---- Completion ---- */

  _completeQuiz() {
    this._updateProgress(this.totalSteps);
    this.stepsEl.hidden = true;

    const recommendations = Object.values(this.answers).map((a) => a.recommendation);

    document.dispatchEvent(
      new CustomEvent('quiz:complete', {
        bubbles: true,
        detail: {
          answers: this.answers,
          recommendations,
        },
      })
    );

    if (this.emailStepEl) {
      this.emailStepEl.hidden = false;
      this.emailStepEl.querySelector('input[type="email"]')?.focus();
    } else {
      this._showResult();
    }
  }

  /* ---- Email capture ---- */

  _bindEmailCapture() {
    if (!this.emailStepEl) return;

    const form = this.emailStepEl.querySelector('.gro-quiz__email-form');
    const skipBtn = this.emailStepEl.querySelector('.js-gro-quiz-skip-email');

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submitEmail(form);
    });

    skipBtn?.addEventListener('click', () => {
      this.emailStepEl.hidden = true;
      this._showResult();
    });
  }

  _submitEmail(form) {
    const recommendations = Object.values(this.answers).map((a) => a.recommendation);

    const noteInput = document.createElement('input');
    noteInput.type = 'hidden';
    noteInput.name = 'contact[note]';
    noteInput.value = `Quiz answers: ${JSON.stringify(this.answers)} | Recommendations: ${recommendations.join(', ')}`;
    form.appendChild(noteInput);

    fetch('/contact', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
    }).catch(() => {
      /* Non-blocking */
    }).finally(() => {
      this.emailStepEl.hidden = true;
      this._showResult();
    });
  }

  /* ---- Scoring and result rendering ---- */

  _showResult() {
    this.resultsEl.hidden = false;
    this.resultsEl.focus();

    if (QUIZ_DEV_MODE) {
      console.info('[QUIZ_DEV_MODE] Showing first result block. Set QUIZ_DEV_MODE = false before pushing.');
      const firstResult = this.container.querySelector('.gro-quiz__result-def');
      this._renderResults(firstResult ? [firstResult] : []);
      return;
    }

    const winnerDefs = this._scoreAndFind();
    this._renderResults(winnerDefs);
  }

  _scoreAndFind() {
    /* Tally a point per answer towards its recommendation */
    const scores = {};
    Object.values(this.answers).forEach(({ recommendation }) => {
      if (recommendation) {
        scores[recommendation] = (scores[recommendation] || 0) + 1;
      }
    });

    if (!Object.keys(scores).length) {
      /* No recommendations mapped - show all result blocks */
      return [...this.container.querySelectorAll('.gro-quiz__result-def')];
    }

    const maxScore = Math.max(...Object.values(scores));
    const winners = Object.keys(scores).filter((k) => scores[k] === maxScore);

    const allResultDefs = [...this.container.querySelectorAll('.gro-quiz__result-def')];
    const matched = allResultDefs.filter((def) => winners.includes(def.dataset.key));

    /* Fall back to all results if nothing matched */
    return matched.length ? matched : allResultDefs;
  }

  _renderResults(resultDefs) {
    this.resultsGridEl.innerHTML = '';

    if (!resultDefs.length) {
      this.resultsGridEl.innerHTML = '<p class="gro-quiz__no-results">No recommendation found - please contact us directly.</p>';
      return;
    }

    resultDefs.forEach((def) => {
      this.resultsGridEl.appendChild(this._buildResultCard(def));
    });
  }

  _buildResultCard(def) {
    const title = def.dataset.title || '';
    const ctaLabel = def.dataset.ctaLabel || 'Book a free consultation';
    const ctaUrl = def.dataset.ctaUrl || '#';
    const imgSrc = def.dataset.image || '';
    const imgAlt = def.dataset.imageAlt || title;
    const descEl = def.querySelector('.gro-quiz__result-desc-src');
    const description = descEl?.textContent?.trim() || '';

    const card = document.createElement('div');
    card.className = 'gro-quiz-result';

    card.innerHTML = `
      ${imgSrc ? `
        <div class="gro-quiz-result__image-wrap">
          <img
            src="${imgSrc}"
            alt="${imgAlt}"
            class="gro-quiz-result__image"
            width="900"
            loading="lazy"
          >
        </div>` : ''
      }
      <div class="gro-quiz-result__body">
        ${title ? `<h3 class="gro-quiz-result__title">${title}</h3>` : ''}
        ${description ? `<p class="gro-quiz-result__description">${description}</p>` : ''}
        <a
          href="${ctaUrl}"
          class="gro-quiz__btn gro-quiz__btn--primary gro-quiz-result__cta"
        >${ctaLabel}</a>
      </div>
    `;

    return card;
  }
}

/* ---- Bootstrap ---- */
document.querySelectorAll('.gro-quiz').forEach((el) => new GroQuiz(el));
